// The verdict of `cancelPlanTasks` (#1898 + #2680 + #2696 + #4037) — the canonical
// answer shape, parsed by every consumer. LIVES HERE, not in `@developerz/db`,
// because the field set is a domain contract, not SQL: the API (`/v1/plans/:id/cancel`),
// the MCP `plan cancel` verb, and the dashboard's `planStopMessage` all read it, and
// "the wire forwards what the cancel wrote" is the rule the schema pins. db imports
// the type, mcp projects to its transport shape, and the dashboard renders off the
// i18n key the discriminator reaches — one source, three doors, no drift.
//
// #4037 ADDS `planStatus` (the plan's `status` AFTER the `status = 'open'` CAS, read
// in the same transaction). A `done` plan keeps `done` here — the cancel CAS
// wrote nothing — and the API surfaces that as `plan_status: 'done'` so
// `planStopMessage` can render `already landed` rather than the `nothing to stop`
// lie a `cancelled` re-cancel prints. The `kind: 'cancelled'` discriminator is
// the same switch branch the older `PlanCancelRefusal` used, so a caller adding
// the `done` arm writes one new `case` and the existing `cancelled` arm
// compiles unchanged.

import { z } from 'zod';
import { TaskIdSchema } from './ids';
import { PlanStatusSchema } from './plan-projection';

/** What a plan-stop did to the plan's children (#1898 + #2696 + #4037). */
export const PlanCancelAppliedSchema = z.object({
  kind: z.literal('cancelled'),
  /** The dispatched children the cancel flipped, with the lease epoch each box held before the bump (04-7). */
  cancelled: z.array(
    z.object({
      taskId: TaskIdSchema,
      previousRunnerId: z.string().nullable(),
      previousLeaseEpoch: z.number().int().nonnegative(),
    }),
  ),
  /**
   * Plan groups this call flipped to `cancelled` (#2696) — held groups on a
   * fully-held plan and the held half of a partially-held plan. `0` for a
   * fully-dispatched plan (every group was moved through its `tasks` row's flip,
   * not the group's own). The dashboard's stop summary pairs this count with
   * `cancelled.length` to tell `N cancelled groups` from `N cancelled tasks`.
   */
  cancelledGroupCount: z.number().int().nonnegative(),
  /** `pr_review` children still holding a merge gate — named, never cancelled. */
  skippedReview: z.array(TaskIdSchema),
  /** Children already terminal when the stop landed — untouched, not an error. */
  alreadyTerminal: z.array(TaskIdSchema),
  /**
   * The plan's `status` AFTER this cancel's CAS (#4037). Read inside the same
   * transaction so the wire value is the post-write value, never a stale read
   * taken before the CAS won. A plan that already landed (`done`, #4018) keeps
   * `done` here.
   */
  planStatus: PlanStatusSchema,
});
export type PlanCancelApplied = z.infer<typeof PlanCancelAppliedSchema>;

/** The verdict of `cancelPlanTasks`: `null` when the plan doesn't exist, this when the cancel write landed. */
export const PlanCancelResultSchema = PlanCancelAppliedSchema;
export type PlanCancelResult = z.infer<typeof PlanCancelResultSchema>;
